import React, {useState, useEffect, useRef} from 'react'
import PubSub from '../Broker'
import '../../../styles/index.scss'

// Module-level cache — updated silently in the background as worker results arrive.
// The warning UI only renders when the user clicks Next (ARTWORK_COVERAGE_BLOCK_NEXT).
let _lastWarnings = {}

// Top banner warning when artwork doesn't fully cover the safezone print area.
// Hidden during design — only shown when the user tries to click Next and coverage fails.
const ArtworkCoverageWarning = () => {
    // visibleWarnings: snapshot of _lastWarnings taken at the moment Next was clicked.
    // Empty by default — banner stays hidden during design.
    const [visibleWarnings, setVisibleWarnings] = useState({})
    const [dismissed, setDismissed] = useState(false)
    const mountedRef = useRef(true)

    useEffect(() => {
        mountedRef.current = true

        // Silently update the background cache — does NOT trigger a render / show the banner.
        const coverageToken = PubSub.subscribe('ARTWORK_COVERAGE_CHECK', (data) => {
            const {side, result} = data || {}
            if (side && result) {
                _lastWarnings = {..._lastWarnings, [side]: result}
            }
        })

        // BG is global — when active it covers ALL sides, clear every cached warning at once.
        const bgActiveToken = PubSub.subscribe('ARTWORK_COVERAGE_BG_ACTIVE', () => {
            _lastWarnings = {}
            if (mountedRef.current) {
                setVisibleWarnings({})
                setDismissed(false)
            }
        })

        // User clicked Next and coverage failed — use authoritative failedSides list from NavBar.
        // NavBar computed this from stageSides (all sides), so it covers unvisited sides too.
        const blockToken = PubSub.subscribe('ARTWORK_COVERAGE_BLOCK_NEXT', (data) => {
            if (!mountedRef.current) return
            const {failedSides: blockedSides} = data || {}
            if (!blockedSides || blockedSides.length === 0) return
            // Build a display snapshot: use cached result if available, otherwise mark as failed
            const snapshot = {}
            blockedSides.forEach((side) => {
                snapshot[side] = _lastWarnings[side] || {ok: false}
            })
            setVisibleWarnings(snapshot)
            setDismissed(false)
        })

        // All sides passed — NavBar navigates away and fires this event to clear the banner first.
        const clearToken = PubSub.subscribe('ARTWORK_COVERAGE_CLEAR', () => {
            if (!mountedRef.current) return
            setVisibleWarnings({})
            setDismissed(false)
        })

        return () => {
            mountedRef.current = false
            PubSub.unsubscribe(coverageToken)
            PubSub.unsubscribe(bgActiveToken)
            PubSub.unsubscribe(blockToken)
            PubSub.unsubscribe(clearToken)
        }
    }, [])

    // Collect all failing sides from the snapshot taken at Next-click time
    const failedSides = Object.entries(visibleWarnings)
        .filter(([, result]) => !result.ok)
        .map(([side]) => side)

    if (failedSides.length === 0 || dismissed) return null

    return (
        <div className="ArtworkCoverageWarning">
            <div className="ArtworkCoverageWarning__header">
                <div className="ArtworkCoverageWarning__title">Attention Required</div>
                <button
                    className="ArtworkCoverageWarning__close"
                    onClick={() => setDismissed(true)}
                    aria-label="Dismiss"
                >
                    ✕
                </button>
            </div>
            <div className="ArtworkCoverageWarning__message">
                The design does not fully cover the grey area on the following surface(s): {failedSides.join(', ')}.
                Please complete these areas before proceeding.
            </div>
        </div>
    )
}

export default ArtworkCoverageWarning
